/* eslint-disable no-unused-vars */
/* eslint-disable no-underscore-dangle */

import { isArray, isNil } from 'lodash'

/**
 * Calcule la distance de Jaro entre deux chaînes de caractères.
 * L'algorithme de Jaro mesure la similarité entre deux chaînes en se basant sur
 * le nombre de caractères correspondants et de transpositions.
 *
 * @param {string} str1 - La première chaîne à comparer
 * @param {string} str2 - La seconde chaîne à comparer
 * @returns {number} Un score entre 0 et 1, où 1 indique une correspondance parfaite
 */
const jaroDistance = (str1, str2) => {
    if (str1 === str2) return 1

    const len1 = str1.length
    const len2 = str2.length
    const maxDist = Math.floor(Math.max(len1, len2) / 2) - 1

    const match1 = new Array(len1).fill(false)
    const match2 = new Array(len2).fill(false)

    let matches = 0
    let transpositions = 0

    // Trouver les matches
    for (let i = 0; i < len1; i++) {
        const start = Math.max(0, i - maxDist)
        const end = Math.min(i + maxDist + 1, len2)

        for (let j = start; j < end; j++) {
            if (match2[j] || str1[i] !== str2[j]) continue
            match1[i] = match2[j] = true
            matches++
            break
        }
    }

    if (matches === 0) return 0

    // Compter les transpositions
    let k = 0
    for (let i = 0; i < len1; i++) {
        if (!match1[i]) continue
        while (!match2[k]) k++
        if (str1[i] !== str2[k]) transpositions++
        k++
    }

    // Formule de Jaro
    return (matches / len1 + matches / len2 + (matches - transpositions / 2) / matches) / 3
}

/**
 * Calcule la distance de Jaro-Winkler entre deux chaînes de caractères.
 * Cette variante de l'algorithme de Jaro donne un bonus aux chaînes qui
 * partagent un préfixe commun (jusqu'à 4 caractères).
 *
 * @param {string} s1 - La première chaîne à comparer
 * @param {string} s2 - La seconde chaîne à comparer
 * @returns {number} Un score entre 0 et 1, où 1 indique une correspondance parfaite
 */
const jaroWinklerDistance = (s1, s2) => {
    const jaro = jaroDistance(s1, s2)
    if (jaro < 0.7) return jaro

    // Bonus pour préfixes communs
    let prefix = 0
    for (let i = 0; i < Math.min(s1.length, s2.length, 4); i++) {
        if (s1[i] === s2[i]) prefix++
        else break
    }

    return jaro + (0.1 * prefix * (1 - jaro))
}

/**
 * Génère tous les n-grammes d'une chaîne donnée avec padding.
 * Un n-gramme est une sous-séquence de n caractères consécutifs.
 * La chaîne est paddée avec des espaces pour capturer les débuts/fins.
 *
 * @param {string} str - La chaîne source pour générer les n-grammes
 * @param {number} n - La taille des n-grammes à générer
 * @returns {string[]} Un tableau contenant tous les n-grammes de la chaîne
 */
const getNgrams = (str, n) => {
    const ngrams = new Set()
    const paddedStr = ' '.repeat(n - 1) + str + ' '.repeat(n - 1)

    for (let i = 0; i <= paddedStr.length - n; i++) {
        ngrams.add(paddedStr.substring(i, i + n))
    }

    return ngrams
}

/**
 * Calcule la similarité entre deux chaînes en utilisant l'analyse de n-grammes.
 * La similarité est basée sur l'indice de Jaccard : intersection / union des n-grammes.
 *
 * @param {string} str1 - La première chaîne à comparer
 * @param {Set<string>} set2NGrams - La seconde chaîne à comparer
 * @param {number} [n=2] - La taille des n-grammes à utiliser (par défaut: bigrammes)
 * @returns {number} Un score entre 0 et 1, où 1 indique une correspondance parfaite
 */
const ngramSimilarity = (str1, set2NGrams, n = 2) => {
    const set1 = getNgrams(str1, n)

    const intersection = set1.intersection(set2NGrams)
    const union = set1.union(set2NGrams)

    return union.size === 0 ? 0 : intersection.size / union.size
}

/**
 * Effectue une recherche hybride combinant plusieurs algorithmes de similarité.
 * Combine les scores de Jaro-Winkler, n-grammes et correspondance exacte avec pondération.
 *
 * @param {Object[]} elements - Tableau d'objets à rechercher, chaque objet doit avoir une propriété _normalizedSearchString (String ou liste de String)
 * @param {string} query - La chaîne de recherche
 * @param {Object} [options={}] - Options de configuration pour la recherche
 * @param {number} [options.maxResults=100] - Nombre maximum de résultats à retourner
 * @param {number} [options.threshold=0.6] - Score minimum pour qu'un élément soit inclus dans les résultats
 * @param {number} [options.ngramSize=2] - Taille des n-grammes pour l'analyse de similarité
 * @returns {string[]} Tableau des chaînes correspondantes, triées par score décroissant
 *
 * @description
 * L'algorithme de scoring utilise :
 * - 40% Jaro-Winkler distance (privilégie les préfixes communs)
 * - 30% similarité n-grammes (capture les sous-chaînes communes)
 * - 30% bonus de correspondance exacte (si query est contenue dans la chaîne)
 */
const hybridSearch = (elements, query, options = {}) => {
    const {
        maxResults,
        threshold = 0.45, // 0.5
        // ngramSize = 2,
    } = options

    if (!query) return isNil(maxResults) ? elements : elements.slice(0, maxResults)

    const results = []

    // const queryNGrams = getNgrams(query, ngramSize)

    const getScore = (str) => {
        const jaroScore = jaroWinklerDistance(str, query) * 0.7 // 0.4
        const ngramScore = 0 // ngramSimilarity(str, queryNGrams, ngramSize) * 0.3
        const containsScore = str.includes(query) ? 0.3 : 0

        return jaroScore + ngramScore + containsScore
    }

    elements.forEach((elem, index) => {
        let score = 0

        const normalizedSearchString = elem._normalizedSearchString ?? ''

        if (isArray(normalizedSearchString)) {
            normalizedSearchString.forEach(str => {
                score = Math.max(score, getScore(str))
            })
        } else {
            score = getScore(normalizedSearchString)
        }

        if (score >= threshold) {
            results.push({ item: elem, score, index })
        }
    })
    const sortedResults = results.sort((a, b) => b.score - a.score)
    const slicedResults = isNil(maxResults) ? sortedResults : sortedResults.slice(0, maxResults)
    return slicedResults.map(result => result.item)
}

export {
    hybridSearch,
}